import { timelogsApi } from '@/api/timelogsApi';
import { yandexTrackerApi } from '@/api/yandexTrackerApi';
import { AddAlertParams } from '@/configs/store/AlertsStore';
import { LogsMode } from '@/features/logsMode';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { Log, YandexUser } from '@/types/yandex';
import { formatDurationIso } from '@/utils/formatDurationIso';
import { formatDurationYandex } from '@/utils/formatDurationYandex';
import { formatError } from '@/utils/formatError';
import { fromISOtoISODate } from '@/utils/fromISOtoISODate';
import { parseDuration } from '@/utils/parseDuration';
import { transformTimelogToWeeklyWorkLog } from '@/utils/transformTimelog';

import { WeeklyWorkLog } from '../../types';

import { OptimisticLogUpdateModel, OptimisticUpdateDependencies } from './OptimisticLogUpdateModel';

export interface LogOperationsDependencies extends OptimisticUpdateDependencies {
  /** Откуда логи: Трекер (legacy) или собственный учёт */
  mode: LogsMode;
  /** Получить пользователя */
  getUser: () => { id: string } | null;
  /** Установить ID заблокированного лога */
  setDisabledLogId: (id: string | null) => void;
  /** Получить выбранного пользователя */
  getSelectedUser: () => YandexUser | null;
  /** Вызвать алерт */
  addAlert: (alert: AddAlertParams) => void;
}

export class LogOperationsModel {
  private optimisticUpdater: OptimisticLogUpdateModel;
  readonly deleteStage = new LoadingStageModel();

  constructor(private dependencies: LogOperationsDependencies) {
    this.optimisticUpdater = new OptimisticLogUpdateModel(dependencies);
  }

  /**
   * Добавляет новый лог
   */
  addLog = async ({
    taskKey,
    duration,
    comment,
    startDate,
    usersIds,
  }: {
    taskKey: string;
    duration: number;
    comment: string;
    startDate?: string;
    usersIds?: string[];
  }) => {
    const user = this.dependencies.getUser();

    if (!user || !duration) return;

    try {
      if (this.dependencies.mode === LogsMode.v2) {
        const newLog = await this._createLogV2({ taskKey, duration, comment, startDate, usersIds });

        if (newLog) {
          this.optimisticUpdater.addLog(newLog, startDate, newLog.start, newLog.durationMinutes);
        }

        return;
      }

      const durationString = formatDurationYandex(duration);

      const response = await yandexTrackerApi.createWorklog({
        taskKey,
        duration: durationString,
        comment,
        startDate,
        userIds: usersIds,
      });

      if (response.items.length === 0) {
        return;
      }

      const selectedUser = this.dependencies.getSelectedUser();
      const logForOptimisticUpdate = this._getLogForOptimisticUpdate(
        response.items,
        usersIds,
        selectedUser,
      );

      if (!logForOptimisticUpdate) return;

      const logDate = logForOptimisticUpdate.start;
      const durationMinutes = parseDuration(logForOptimisticUpdate.duration);

      const newLog: WeeklyWorkLog = {
        id: String(logForOptimisticUpdate.id),
        taskKey,
        taskTitle: logForOptimisticUpdate.task.title || 'Неизвестная задача',
        duration: logForOptimisticUpdate.duration,
        durationMinutes,
        comment: logForOptimisticUpdate.comment || comment,
        date: logDate,
        createdAt: logForOptimisticUpdate.createdAt,
        start: logForOptimisticUpdate.start,
      };

      this.optimisticUpdater.addLog(newLog, startDate, logDate, durationMinutes);
    } catch (err) {
      console.error('Ошибка при добавлении лога:', err);

      this._alertError({
        error: err,
        fallback: { message: 'Не удалось добавить лог', autoClose: true },
      });
    }
  };

  /**
   * Обновляет существующий лог
   */
  updateLog = async ({
    workLogId,
    taskKey,
    duration,
    comment,
    startDate,
  }: {
    workLogId: string;
    taskKey: string;
    duration: number;
    comment: string;
    startDate?: string;
  }) => {
    const user = this.dependencies.getUser();

    if (!user || !duration) return;

    this.dependencies.setDisabledLogId(workLogId);

    try {
      if (this.dependencies.mode === LogsMode.v2) {
        // В v2 дата меняется на месте, без «создать + удалить».
        // Форма шлёт '' вместо даты, если её не меняли: такой start не передаём
        const updated = await timelogsApi.updateTimelog({
          id: workLogId,
          data: { duration: formatDurationIso(duration), comment, start: startDate || undefined },
        });

        await this._applyUpdatedLog(transformTimelogToWeeklyWorkLog(updated));
      } else if (!startDate) {
        // Обычное обновление лога без изменения даты
        const durationString = formatDurationYandex(duration);

        const updatedLogFromApi = await yandexTrackerApi.updateWorkLog({
          taskKey,
          workLogId,
          duration: durationString,
          comment,
        });

        const current = await this.dependencies.getCurrentTimesheetData();

        if (!current) return;

        const logDate = updatedLogFromApi.start;
        const durationMinutes = parseDuration(updatedLogFromApi.duration);

        const updatedLog: WeeklyWorkLog = {
          id: workLogId,
          taskKey,
          taskTitle: updatedLogFromApi.issue.display || 'Неизвестная задача',
          duration: updatedLogFromApi.duration,
          durationMinutes,
          comment,
          date: logDate,
          createdAt: updatedLogFromApi.createdAt,
          start: updatedLogFromApi.start,
        };

        // Наход старый лог для получения его даты и продолжительности
        let oldLogDate = '';
        let oldDuration = 0;

        for (const date in current.days) {
          const oldLog = current.days[date].find((log) => log.id === workLogId);

          if (oldLog) {
            oldLogDate = date;
            oldDuration = oldLog.durationMinutes;

            break;
          }
        }

        if (oldLogDate) {
          this.optimisticUpdater.updateLog({
            updatedLog,
            oldLogDate,
            newLogDate: fromISOtoISODate(logDate),
            oldDurationMinutes: oldDuration,
            newDurationMinutes: durationMinutes,
          });
        }
      } else {
        // добавить + удалить (даунская апишка трекера)
        // Передаём выбранного пользователя, чтобы при редактировании чужого лога админом новый лог создавался от имени того, чей лог редактируется
        const selectedUser = this.dependencies.getSelectedUser();
        const usersIds = selectedUser ? [selectedUser.uid] : undefined;
        await this.addLog({ taskKey, duration, comment, startDate, usersIds });
        await this.deleteLog({ taskKey, id: workLogId });
      }
    } catch (err) {
      console.error('Ошибка при обновлении лога:', err);

      this._alertError({
        error: err,
        fallback: { message: 'Не удалось обновить лог', autoClose: true },
      });
    }

    this.dependencies.setDisabledLogId(null);
  };

  /**
   * Удаляет лог
   */
  deleteLog = async ({ taskKey, id }: { taskKey: string; id: string }) => {
    const user = this.dependencies.getUser();

    if (!user) return;

    this.dependencies.setDisabledLogId(id);

    try {
      this.deleteStage.loading();

      if (this.dependencies.mode === LogsMode.v2) {
        await timelogsApi.deleteTimelog(id);
      } else {
        await yandexTrackerApi.deleteWorkLog({
          taskKey,
          workLogId: id,
        });
      }

      // Наход лог для оптимистичного удаления
      const current = await this.dependencies.getCurrentTimesheetData();

      if (!current) return;

      let logDate = '';
      let removedDuration = 0;

      // Ищем лог во всех днях для получения его даты и продолжительности
      for (const date in current.days) {
        const log = current.days[date].find((l) => l.id === id);

        if (log) {
          logDate = date;
          removedDuration = log.durationMinutes;

          break;
        }
      }

      if (logDate && removedDuration) {
        this.optimisticUpdater.removeLog(id, logDate, removedDuration);
      }

      this.deleteStage.success();
    } catch (err) {
      this.deleteStage.error();

      console.error('Ошибка при удалении лога:');

      this._alertError({
        error: err,
        fallback: { message: 'Не удалось удалить лог', title: 'Ошибка', autoClose: true },
      });
    }

    this.dependencies.setDisabledLogId(null);
  };

  /**
   * В собственном учёте бэк сам объясняет отказ (больше 24 часов, нет доступа к очереди) —
   * показываем его текст. В Трекере остаётся прежнее общее сообщение
   */
  private _alertError({ error, fallback }: { error: unknown; fallback: AddAlertParams }) {
    if (this.dependencies.mode !== LogsMode.v2) {
      this.dependencies.addAlert(fallback);

      return;
    }

    const { title, message } = formatError(error);

    this.dependencies.addAlert({ title, message, withReload: false, autoClose: true });
  }

  /**
   * Создаёт лог в собственном учёте; за нескольких пользователей — одной транзакцией.
   * Возвращает лог просматриваемого пользователя: только он виден на экране
   */
  private async _createLogV2({
    taskKey,
    duration,
    comment,
    startDate,
    usersIds,
  }: {
    taskKey: string;
    duration: number;
    comment: string;
    startDate?: string;
    usersIds?: string[];
  }): Promise<WeeklyWorkLog | null> {
    const start = startDate || new Date().toISOString();
    const owners = usersIds?.length ? usersIds : [undefined];

    const timelogs = await timelogsApi.createTimelogs(
      owners.map((user) => ({
        task: taskKey,
        start,
        duration: formatDurationIso(duration),
        comment,
        user,
      })),
    );

    const selectedUser = this.dependencies.getSelectedUser();
    const shown = timelogs.find((log) => log.user.id === selectedUser?.uid);

    return shown ? transformTimelogToWeeklyWorkLog(shown) : null;
  }

  /** Переносит изменённый лог на его новое место в данных экрана */
  private async _applyUpdatedLog(updatedLog: WeeklyWorkLog) {
    const current = await this.dependencies.getCurrentTimesheetData();

    if (!current) return;

    const oldEntry = Object.entries(current.days).find(([, logs]) =>
      logs.some((log) => log.id === updatedLog.id),
    );
    const oldLog = oldEntry?.[1].find((log) => log.id === updatedLog.id);

    if (!oldEntry || !oldLog) return;

    // Перенесли за пределы периода: здесь лог пропадает, на новом месте его покажет загрузка
    if (!(updatedLog.date in current.days)) {
      await this.optimisticUpdater.removeLog(updatedLog.id, oldEntry[0], oldLog.durationMinutes);

      return;
    }

    this.optimisticUpdater.updateLog({
      updatedLog,
      oldLogDate: oldEntry[0],
      newLogDate: updatedLog.date,
      oldDurationMinutes: oldLog.durationMinutes,
      newDurationMinutes: updatedLog.durationMinutes,
    });
  }

  /**
   * Определяет лог для оптимистичного обновления на основе выбранного пользователя и списка пользователей
   */
  private _getLogForOptimisticUpdate(
    logs: Log[],
    usersIds: string[] | undefined,
    selectedUser: { uid: string } | null,
  ): Log | null {
    if (selectedUser && usersIds && usersIds.includes(selectedUser.uid)) {
      // Если выбранный пользователь есть в списке пользователей для логирования,
      // ищем лог, созданный именно этим пользователем
      const log = logs.find((log) => String(log.createdBy.id) === String(selectedUser.uid));

      return log || null;
    }

    if (!usersIds || usersIds.length === 0) {
      // Если массива userIds нет, логаем за авторизованного пользователя
      return logs[0] || null;
    }

    return null;
  }
}
